import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { employeeService } from "../../services/employeeService";
import { AuthContext } from "../../contexts/AuthContext";
import { getRolePrefix } from "../../utils/routeUtils";
import Notification from "../../components/UI/Notification";
import StatusBadge from "../../components/UI/StatusBadge";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  IndianRupee,
  Building2,
  FileText,
  MapPin,
} from "lucide-react";

const EmployeeProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { roles } = useContext(AuthContext);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState("personal");

  useEffect(() => {
    fetchEmployeeDetails();
  }, [id]);

  const fetchEmployeeDetails = async () => {
    setLoading(true);
    try {
      const response = await employeeService.getEmployee(id);

      // Handle API response structure
      let employeeData = null;
      if (response && response.success !== undefined) {
        employeeData = response.data || response;
      } else if (response) {
        employeeData = response;
      }

      if (employeeData) {
        setEmployee(employeeData);
      } else {
        throw new Error("Employee data not found");
      }
    } catch (error) {
      console.error("Error fetching employee details:", error);
      setNotification({
        type: "error",
        message:
          error.response?.data?.message || "Failed to fetch employee details",
      });
    } finally {
      setLoading(false);
    }
  };

  // Get initials for avatar
  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || "";
    const last = lastName?.charAt(0)?.toUpperCase() || "";
    return first + last || "??";
  };

  // Format date to "1 March 2021" format
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  // Format currency to Indian format
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "N/A";
    return `₹${Number(amount).toLocaleString("en-IN")}`;
  };

  // Mask account number (show last 4 digits)
  const maskAccountNumber = (accountNumber) => {
    if (!accountNumber) return "N/A";
    const str = String(accountNumber);
    if (str.length <= 4) return str;
    return `****${str.slice(-4)}`;
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="text-center py-20">
          <p className="text-gray-500 dark:text-gray-400">Employee not found</p>
          <button
            onClick={() => {
              const rolePrefix = getRolePrefix(roles);
              navigate(`${rolePrefix}/payroll/employee-management`);
            }}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Back to Employees
          </button>
        </div>
      </div>
    );
  }

  const firstName = employee.first_name || "";
  const lastName = employee.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim() || "N/A";
  const employeeId =
    employee.employee_id || employee.employment_id || employee.id || "N/A";
  const department = employee.department_name || employee.department || "N/A";
  const designation =
    employee.designation_name || employee.designation || "N/A";
  const email = employee.email || "N/A";
  const phone = employee.phone || employee.phone_number || "N/A";
  const dateOfBirth = formatDate(employee.date_of_birth);
  const dateOfJoining = formatDate(
    employee.date_of_joining || employee.joining_date,
  );
  const reportingTo =
    employee.reporting_to || employee.reporting_to_name || "N/A";
  const employmentType =
    employee.employment_type || employee.employment_type_name || "N/A";
  const status = employee.status || "Active";

  const tabs = [
    { id: "personal", label: "Personal", icon: <User size={18} /> },
    { id: "salary", label: "Salary", icon: <IndianRupee size={18} /> },
    { id: "bank", label: "Bank", icon: <Building2 size={18} /> },
    { id: "statutory", label: "Statutory", icon: <FileText size={18} /> },
    { id: "address", label: "Address", icon: <MapPin size={18} /> },
  ];

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => {
            const rolePrefix = getRolePrefix(roles);
            navigate(`${rolePrefix}/payroll/employee-management`);
          }}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Employees</span>
        </button>
      </div>

      {/* Employee Summary Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between gap-6">
          <div className="flex flex-1 items-start gap-6">
            {/* Avatar */}
            <div className="w-20 h-20 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center text-teal-600 dark:text-teal-400 font-semibold text-2xl shrink-0">
              {getInitials(firstName, lastName)}
            </div>

            {/* Employee Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {fullName}
                </h1>
                <StatusBadge status={status} />
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
                {designation} • {department}
              </p>

              <div className="flex flex-wrap gap-6 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2">
                  <Mail size={16} />
                  <span>{email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={16} />
                  <span>{phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  <span>Joined {dateOfJoining}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Top Metadata */}
          <div className="flex flex-col items-start md:items-end gap-3 md:pl-6 md:border-l border-gray-100 dark:border-gray-700">
            <div className="text-left md:text-right">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                Employee ID
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {employeeId}
              </p>
            </div>
            <div className="flex items-center gap-2 mt-auto">
              <span className="text-xs text-gray-500 uppercase tracking-wider">
                Profile Status:
              </span>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  employee.profile_complete === true
                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
                }`}
              >
                {employee.profile_complete === true ? "Complete" : "Incomplete"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 font-medium text-sm transition-colors border-b-2 ${
                activeTab === tab.id
                  ? tab.id === "bank"
                    ? "border-green-500 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700"
                    : "border-blue-600 text-blue-600 dark:text-blue-400 bg-gray-50 dark:bg-gray-700"
                  : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content Panels - Personal Tab */}
      {activeTab === "personal" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              Basic Information
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    First Name
                  </p>
                  <p className="text-gray-900 dark:text-white">
                    {firstName || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Last Name
                  </p>
                  <p className="text-gray-900 dark:text-white">
                    {lastName || "N/A"}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Email
                </p>
                <p className="text-gray-900 dark:text-white">{email}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Phone
                </p>
                <p className="text-gray-900 dark:text-white">{phone}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Date of Birth
                </p>
                <p className="text-gray-900 dark:text-white">{dateOfBirth}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Date of Joining
                </p>
                <p className="text-gray-900 dark:text-white">{dateOfJoining}</p>
              </div>
            </div>
          </div>

          {/* Employment Details */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              Employment Details
            </h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Department
                </p>
                <p className="text-gray-900 dark:text-white">{department}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Designation
                </p>
                <p className="text-gray-900 dark:text-white">{designation}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Reporting To
                </p>
                <p className="text-gray-900 dark:text-white">{reportingTo}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Employment Type
                </p>
                <p className="text-gray-900 dark:text-white">
                  {employmentType}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Status
                </p>
                <StatusBadge status={status} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Salary Tab */}
      {activeTab === "salary" &&
        (() => {
          const totalEarnings = employee.salary_breakdown
            ? Object.entries(employee.salary_breakdown)
                .filter(([k]) =>
                  [
                    "basic_salary",
                    "hra",
                    "transport_allowance",
                    "medical_allowance",
                    "special_allowance",
                    "other_allowances",
                    "dearness_allowance",
                    "bonus",
                    "gratuity",
                    "lta",
                    "basic",
                    "bo",
                    "da",
                    "coy",
                    "sp_all",
                    "basic_da",
                    "hr",
                    "s_all",
                    "te",
                  ].includes(k),
                )
                .reduce((acc, [_, v]) => acc + (parseFloat(v) || 0), 0)
            : Number(employee.gross_salary || employee.salary || 0);

          const totalDeductions = employee.salary_breakdown
            ? Object.entries(employee.salary_breakdown)
                .filter(([k]) =>
                  [
                    "pf_employee",
                    "esi_employee",
                    "professional_tax",
                    "income_tax",
                  ].includes(k),
                )
                .reduce((acc, [_, v]) => acc + (parseFloat(v) || 0), 0)
            : Number(employee.pf_employee || 0) +
              Number(employee.esi_employee || 0) +
              Number(employee.professional_tax || 0);

          const netSalary = employee.net_salary;
          const annualCTC = employee.ctc || totalEarnings * 12;

          return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Salary Components */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                  Salary Components
                </h2>

                {/* Earnings */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Earnings
                  </h3>
                  <div className="space-y-3">
                    {employee.salary_breakdown &&
                      (() => {
                        const earnings = Object.entries(
                          employee.salary_breakdown,
                        ).filter(([key, value]) => {
                          const isEarning = [
                            "basic_salary",
                            "hra",
                            "transport_allowance",
                            "medical_allowance",
                            "special_allowance",
                            "other_allowances",
                            "dearness_allowance",
                            "bonus",
                            "gratuity",
                            "lta",
                            "basic",
                            "bo",
                            "da",
                            "coy",
                            "sp_all",
                            "basic_da",
                            "hr",
                            "s_all",
                            "te",
                          ].includes(key);
                          return isEarning && (parseFloat(value) || 0) !== 0;
                        });

                        // Deduplicate: If we have both 'basic_salary' and 'basic', keep only 'basic_salary'
                        const deduplicated = [];
                        const priorities = {
                          basic_salary: "basic",
                          hra: "hr",
                          transport_allowance: "coy",
                          bonus: "bo",
                          dearness_allowance: "da",
                          special_allowance: "sp_all",
                        };

                        earnings.forEach(([k, v]) => {
                          const isLegacy =
                            Object.values(priorities).includes(k);
                          if (isLegacy) {
                            const mainKey = Object.keys(priorities).find(
                              (pk) => priorities[pk] === k,
                            );
                            if (earnings.some(([ek]) => ek === mainKey)) return;
                          }
                          deduplicated.push([k, v]);
                        });

                        return deduplicated.map(([key, value]) => (
                          <div
                            key={key}
                            className="flex justify-between items-center group"
                          >
                            <span className="text-gray-700 dark:text-gray-300 capitalize">
                              {key.replace(/_/g, " ")}
                            </span>
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(value)}
                            </span>
                          </div>
                        ));
                      })()}
                  </div>
                </div>

                {/* Deductions */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Deductions
                  </h3>
                  <div className="space-y-3">
                    {employee.salary_breakdown &&
                      (() => {
                        const deductions = Object.entries(
                          employee.salary_breakdown,
                        ).filter(([key, value]) => {
                          const isDeduction = [
                            "pf_employee",
                            "esi_employee",
                            "professional_tax",
                            "income_tax",
                            "pt",
                          ].includes(key);
                          return isDeduction && (parseFloat(value) || 0) !== 0;
                        });

                        return deductions.map(([key, value]) => (
                          <div
                            key={key}
                            className="flex justify-between items-center group"
                          >
                            <span className="text-gray-700 dark:text-gray-300 capitalize">
                              {key.replace(/_/g, " ")}
                            </span>
                            <span className="font-semibold text-red-600 dark:text-red-400">
                              -{formatCurrency(value)}
                            </span>
                          </div>
                        ));
                      })()}
                  </div>
                </div>
              </div>

              {/* Salary Summary */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                  Salary Summary
                </h2>
                <div className="space-y-6">
                  {/* Net Salary Box */}
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border border-green-200 dark:border-green-800">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                      Net Salary (Monthly)
                    </p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(netSalary)}
                    </p>
                  </div>

                  {/* Other Summary Items */}
                  <div className="space-y-4">
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border border-green-200 dark:border-green-800">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Annual CTC
                      </p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(annualCTC)}
                      </p>
                    </div>
                    {employee.pf_employer && (
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                          PF (Employer)
                        </p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(employee.pf_employer)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Bank Tab */}
      {activeTab === "bank" && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            Bank Account Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                Account Holder Name
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {employee.account_holder_name || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                Account Number
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {maskAccountNumber(employee.account_number)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                Bank Name
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {employee.bank_name || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                Branch Name
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {employee.branch_name || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                IFSC Code
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {employee.ifsc_code || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                Account Type
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {employee.account_type || "N/A"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statutory Tab */}
      {activeTab === "statutory" && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            Statutory Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                PAN Number
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {employee.pan_number || "—"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                Aadhar Number
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {employee.aadhar_number || "—"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                UAN Number
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {employee.uan_number || "—"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                ESI Number
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {employee.esi_number || "—"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Address Tab */}
      {activeTab === "address" && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            Address Information
          </h2>
          <div className="space-y-6">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                Current Address
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {employee.current_address || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                Permanent Address
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {employee.permanent_address || "N/A"}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  City
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {employee.city || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  State
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {employee.state || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  PIN Code
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {employee.pin_code || "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeProfile;
